///
/// EarthBrowser
///
/// Created by Matt Giger on 10/21/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"

/**
 * One storm-centered ~2 km infrared cloud frame (the "storms" shard of the
 * clouds dataset; see weather-services storm-clouds.ts). Each frame carries
 * its own lat/lon box because the box follows the storm.
 */
export interface StormCloudProperties {
	storm_id: string
	time: string
	url: string
	satellite: string
	min_lon: number
	min_lat: number
	max_lon: number
	max_lat: number
	width: number
	height: number
}

const cBatchSize = 500

export class StormCloudsTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists storm_clouds (
					storm_id text not null,
					time text not null,
					url text not null,
					satellite text not null,
					min_lon real not null,
					min_lat real not null,
					max_lon real not null,
					max_lat real not null,
					width integer not null,
					height integer not null,
					primary key (storm_id, time)
				)`
			},
			{ sql: "create index if not exists idx_storm_clouds_time on storm_clouds(time desc)" },
		])
	}

	public static async replaceAll(rows: StormCloudProperties[]): Promise<void> {
		await Database.db.exec("delete from storm_clouds", [], "critical")
		const statement = `insert into storm_clouds
			(storm_id, time, url, satellite, min_lon, min_lat, max_lon, max_lat, width, height)
			values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
			on conflict(storm_id, time) do update set
				url = excluded.url, satellite = excluded.satellite,
				min_lon = excluded.min_lon, min_lat = excluded.min_lat,
				max_lon = excluded.max_lon, max_lat = excluded.max_lat,
				width = excluded.width, height = excluded.height`
		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(r => [
				r.storm_id, r.time, r.url, r.satellite,
				r.min_lon, r.min_lat, r.max_lon, r.max_lat,
				r.width, r.height,
			]))
		}
	}

	/** One storm's frames newer than the cutoff, oldest first (for the storm column lid) */
	public static async getForStorm(stormId: string, cutoffIso: string): Promise<StormCloudProperties[]> {
		// User-initiated (the storm column waits on it): ahead of background ingest
		const rows = await Database.db.exec(
			"select * from storm_clouds where storm_id = ? and time >= ? order by time asc",
			[stormId, cutoffIso],
			"high"
		)
		return rows as StormCloudProperties[]
	}

	/** Frames newer than the cutoff, newest first */
	public static async getSince(cutoffIso: string): Promise<StormCloudProperties[]> {
		const rows = await Database.db.exec(
			"select * from storm_clouds where time >= ? order by time desc",
			[cutoffIso],
			"low"
		)
		return rows as StormCloudProperties[]
	}
}
